import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  Snippet,
  SnippetsSharingAccess,
  SnippetType,
} from '@distribute/shared/types';
import {
  snippetsModel,
  useSnippetCommonActions,
} from '../../../features/snippets';

import { SNIPPETS_TEAM_ROUTE } from '../../../entities/history';

import { IconMap } from '../../../shared/sprite';
import { useSnippetsPermissions } from '../../../features/teams';
import { DropdownItem } from '../../../shared/ui/Dropdown';
import { useShareLink } from '../../../entities/share';

type Props = {
  snippet: Snippet;
};

export const useSnippetAction = ({ snippet }: Props) => {
  const { handleCopyShareLink } = useShareLink();

  const { handleEditSnippet, handlePreviewSnippet } = useSnippetCommonActions();

  const dispatch = useDispatch();

  const { isCanManageSnippets } = useSnippetsPermissions();

  const isTeam = snippet.sharingAccess === SnippetsSharingAccess.TEAM;
  const isSettingsAvailable =
    snippet.sharingAccess === SnippetsSharingAccess.PERSONAL ||
    isCanManageSnippets;

  const snippetOptions = useMemo<DropdownItem[]>(() => {
    return isSettingsAvailable
      ? [
          {
            id: 'preview',
            label: 'Preview',
            onClick: () => handlePreviewSnippet(snippet),
            iconName: IconMap.Eye,
            iconWidth: 16,
          },
          {
            id: 'share',
            label: 'Share with team',
            onClick: () =>
              handleCopyShareLink({
                path: SNIPPETS_TEAM_ROUTE,
                id: snippet.id,
              }),
            iconName: IconMap.Share07,
            iconWidth: 16,
            isHide: !isTeam,
          },
          {
            id: 'edit',
            label: snippet.type === SnippetType.TEXT ? 'Edit' : 'Replace',
            iconName:
              snippet.type === SnippetType.TEXT
                ? IconMap.Edit02
                : IconMap.RefreshCw05,
            iconWidth: 16,
            onClick: () => handleEditSnippet(snippet),
            isSeparatedFromTop: true,
          },
          {
            id: 'settings',
            label: 'Settings',
            onClick: () =>
              dispatch(
                snippetsModel.actions.setModal({
                  type: 'snippetSettings',
                  snippet,
                })
              ),
            iconName: IconMap.Settings,
            iconWidth: 16,
            isHide: snippet.isDraft,
          },
          {
            id: 'delete',
            label: 'Delete',
            onClick: () => {
              if (snippet.isDraft) {
                dispatch(
                  snippetsModel.actions.deleteSnippet({
                    id: snippet.id,
                    sharingAccess: snippet.sharingAccess,
                    isDraft: Boolean(snippet.isDraft),
                    callback: () => {
                      return;
                    },
                  })
                );
                return;
              }

              dispatch(
                snippetsModel.actions.setModal({
                  type: 'deleteSnippet',
                  snippet,
                })
              );
            },
            iconName: IconMap.Delete,
            iconWidth: 16,
          },
        ].filter((i) => !i.isHide)
      : [
          {
            id: 'preview',
            label: 'Preview',
            onClick: () => handlePreviewSnippet(snippet),
            iconName: IconMap.Eye,
            iconWidth: 16,
          },
          {
            id: 'share',
            label: 'Share with team',
            onClick: () =>
              handleCopyShareLink({
                path: SNIPPETS_TEAM_ROUTE,
                id: snippet.id,
              }),
            iconName: IconMap.Share07,
            iconWidth: 16,
          },
        ];
  }, [
    dispatch,
    snippet,
    handleEditSnippet,
    handlePreviewSnippet,
    handleCopyShareLink,
    isSettingsAvailable,
    isTeam,
  ]);

  return {
    handlePreviewSnippet,
    handleEditSnippet,
    snippetOptions,
    isSettingsAvailable,
  };
};
